import React from 'react'
import { observer } from 'mobx-react-lite'

import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Icon, Text } from '@mxenabled/mxui'
import {
  CheckCircle as CheckCircleRoundedIcon,
  Error as ErrorIcon,
  Language as LanguageRoundedIcon,
} from '@mxenabled/mx-icons'

import ConnectDrawer from '../../../../common/components/ConnectDrawer'
import { IconBacking, useGlobalCopyStore } from '../../../../common'
import { NonConnectedStatuses } from '../../../../core'
import { getConnectionMessages } from '../../../utils/getConnectionMessages'
import { useAccountUiStore } from '../../../hooks/useUiStore'

const iconStyle = {
  color: 'background.default',
  fontSize: 20,
  gridRow: 1,
  gridColumn: 1,
  zIndex: 2,
}

const AccountConnection = () => {
  const [shouldShowConnectDrawer, setShouldShowConnectDrawer] = React.useState(false)
  const {
    palette: { mode },
  } = useTheme()
  const { selectedMember: member } = useAccountUiStore()
  const { accounts: accountsCopy, connect: connectCopy } = useGlobalCopyStore()

  const statusMessages = getConnectionMessages(member, connectCopy)

  const status = NonConnectedStatuses.includes(member.connection_status)
    ? {
        color: 'error.main',
        ctaCopy: statusMessages.cta ?? '',
        icon: <ErrorIcon filled={true} sx={{ ...iconStyle }} />,
        mainMessage: statusMessages.statusName,
        secondaryMessage: `${statusMessages.title}.`,
      }
    : {
        color: 'success.main',
        icon: <CheckCircleRoundedIcon filled={true} sx={{ ...iconStyle }} />,
        mainMessage: statusMessages.statusName,
        secondaryMessage: statusMessages.title,
      }

  return (
    <>
      <ListSubheader sx={{ mb: 4, mt: 12, mx: 8 }}>
        <Text bold={true} component="h2" variant="caption">
          {accountsCopy.connection_status}
        </Text>
      </ListSubheader>
      <ListItem
        sx={{
          backgroundColor: 'background.paper',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            right: 0,
            left: member.is_manual || !member.institution_url ? 0 : 24,
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {member.is_manual || !status.ctaCopy ? (
          <Stack alignItems="center" direction="row" px={24} py={12}>
            <ListItemAvatar>
              <IconBacking backingSx={{ backgroundColor: status.color, borderRadius: 1 }} size={32}>
                {status.icon}
              </IconBacking>
            </ListItemAvatar>
            <ListItemText primary={status.mainMessage} secondary={status.secondaryMessage} />
          </Stack>
        ) : (
          <ListItemButton onClick={() => setShouldShowConnectDrawer(true)}>
            <ListItemAvatar>
              <IconBacking backingSx={{ backgroundColor: status.color, borderRadius: 1 }} size={32}>
                {status.icon}
              </IconBacking>
            </ListItemAvatar>
            <ListItemText
              primary={status.mainMessage}
              secondary={
                status.secondaryMessage && (
                  <>
                    {status.secondaryMessage}{' '}
                    <Text bold={true} sx={{ color: 'action.active' }} variant="caption">
                      {status.ctaCopy}
                    </Text>
                  </>
                )
              }
            />
            <Icon name="chevron_right" />
          </ListItemButton>
        )}
      </ListItem>

      {!member.is_manual && member.institution_url && (
        <>
          <ListItem
            sx={{
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ListItemButton component="a" href={member.institution_url} target="_blank">
              <ListItemAvatar>
                <IconBacking
                  backingSx={{
                    backgroundColor: mode === 'light' ? 'grey.300' : 'grey.700',
                    borderRadius: 1,
                  }}
                  size={32}
                >
                  <LanguageRoundedIcon
                    sx={{
                      ...iconStyle,
                      color: mode === 'light' ? 'grey.900' : 'common.white',
                    }}
                  />
                </IconBacking>
              </ListItemAvatar>
              <ListItemText primary={member.name} secondary={member.institution_url} />
              <Icon name="launch" />
            </ListItemButton>
          </ListItem>
        </>
      )}

      {shouldShowConnectDrawer && (
        <ConnectDrawer
          loadWidgetProps={{ current_member_guid: member.guid }}
          onClose={() => setShouldShowConnectDrawer(false)}
          showConnectWidget={shouldShowConnectDrawer}
          title={accountsCopy.manage_connection}
        />
      )}
    </>
  )
}

export default observer(AccountConnection)
